import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import { ShippingConfiguration } from '@/hooks'
import { ShippingCondition } from '@/application/manage-shipping'
import { useStoreCurrency } from '@/hooks/use-store-currency'
import { formatCurrency } from '@/utils'
import {
  FieldGroup,
  OpenKey,
  OperatorSegment,
  SetOpenKey,
  TagsPicker,
  ValueSegment,
  bindOpen
} from '@/ui/components/rule-list'
import {
  ALWAYS,
  FIELD_GROUPS,
  FieldKind,
  fieldSpec,
  isComplete
} from './shipping-rules'

const { Input, FieldRoot, FieldLabel, SegmentedChip } = Base

const DEFAULT_COMPARISONS: Record<FieldKind, string[]> = {
  list: ['In', 'NotIn'],
  tags: ['In', 'NotIn'],
  boolean: ['Is', 'NotIs'],
  number: [
    'GreaterThan',
    'GreaterThanOrEqual',
    'LessThan',
    'LessThanOrEqual',
    'Equal',
    'NotEqual'
  ],
  money: [
    'GreaterThan',
    'GreaterThanOrEqual',
    'LessThan',
    'LessThanOrEqual',
    'Equal',
    'NotEqual'
  ],
  date: ['After', 'AfterOrEqual', 'Before', 'BeforeOrEqual']
}

export const comparisonsOf = (fn: string, config: ShippingConfiguration) => {
  const offered = config.functions.find((f) => f.function === fn)?.comparison
  return offered?.length ? offered : DEFAULT_COMPARISONS[fieldSpec(fn).kind]
}

// Labels, as a rule holds them; values a rule already holds stay pickable.
const optionsOf = (
  fn: string,
  config: ShippingConfiguration,
  current: string[]
) => {
  const values = config.functions.find((f) => f.function === fn)?.values ?? []
  return [...new Set([...values, ...current])].map((value) => ({
    value,
    label: value
  }))
}

export function useFieldLabel() {
  const { t } = useTranslation()
  return (fn: string) =>
    fn === ALWAYS
      ? t('shipping_rules.always')
      : t(`edit_shipping.function_names.${fn}`, { defaultValue: fn })
}

function useSummary() {
  const { t } = useTranslation()
  const currency = useStoreCurrency()
  return (condition: ShippingCondition) => {
    const { kind } = fieldSpec(condition.function)
    if (!isComplete(condition)) return t('shipping_rules.choose')
    if (kind === 'boolean') return t('shipping_rules.true_value')
    if (kind === 'money') return formatCurrency(condition.values[0], currency)
    if (kind === 'number' || kind === 'date') return condition.values[0]
    const names = condition.values
    return names.length <= 4
      ? names.join(', ')
      : t('shipping_rules.and_more', {
          values: names.slice(0, 3).join(', '),
          count: names.length - 3
        })
  }
}

export const ConditionChip: React.FC<{
  id: string
  condition: ShippingCondition
  availableConfiguration: ShippingConfiguration
  editable: boolean
  openKey: OpenKey
  setOpenKey: SetOpenKey
  onChange: (condition: ShippingCondition) => void
  onRemove: () => void
}> = ({
  id,
  condition,
  availableConfiguration,
  editable,
  openKey,
  setOpenKey,
  onChange,
  onRemove
}) => {
  const { t } = useTranslation()
  const summary = useSummary()
  const fieldLabel = useFieldLabel()
  const { kind } = fieldSpec(condition.function)
  const label = fieldLabel(condition.function)
  const bind = (part: string) => bindOpen(`${id}:${part}`, openKey, setOpenKey)
  const setValues = (values: string[]) => onChange({ ...condition, values })

  return (
    <SegmentedChip.Root invalid={!isComplete(condition)}>
      <SegmentedChip.Segment emphasis="label">{label}</SegmentedChip.Segment>
      <OperatorSegment
        label={t('shipping_rules.operator_label', { field: label })}
        value={condition.comparison}
        options={comparisonsOf(condition.function, availableConfiguration).map(
          (comparison) => ({
            value: comparison,
            label: t(`shipping_rules.operators.${comparison}`, {
              defaultValue: comparison
            })
          })
        )}
        editable={editable}
        {...bind('cmp')}
        onChange={(comparison) => onChange({ ...condition, comparison })}
      />
      {kind === 'boolean' ? (
        <SegmentedChip.Segment>{summary(condition)}</SegmentedChip.Segment>
      ) : (
        <ValueSegment
          label={t('shipping_rules.value_label', { field: label })}
          summary={summary(condition)}
          title={
            condition.values.length > 1
              ? condition.values.join(', ')
              : undefined
          }
          className="max-w-md"
          editable={editable}
          {...bind('value')}
          list={
            kind === 'list'
              ? {
                  options: optionsOf(
                    condition.function,
                    availableConfiguration,
                    condition.values
                  ),
                  value: condition.values,
                  onChange: setValues
                }
              : undefined
          }
        >
          {kind === 'tags' ? (
            <TagsPicker
              label={label}
              values={condition.values}
              onChange={setValues}
            />
          ) : kind === 'list' ? null : (
            <ValueInput
              label={label}
              kind={kind}
              value={condition.values[0] ?? ''}
              onChange={(value) => setValues(value === '' ? [] : [value])}
              onDone={() => setOpenKey(null)}
            />
          )}
        </ValueSegment>
      )}
      {editable ? (
        <SegmentedChip.Remove
          removeLabel={t('shipping_rules.remove_condition', { field: label })}
          onClick={onRemove}
        />
      ) : null}
    </SegmentedChip.Root>
  )
}

// Amounts stay in currency units: useSaveShippingConfig converts them to cents.
const ValueInput: React.FC<{
  label: string
  kind: 'number' | 'money' | 'date'
  value: string
  onChange: (value: string) => void
  onDone: () => void
}> = ({ label, kind, value, onChange, onDone }) => (
  <FieldRoot className="p-3">
    <FieldLabel>{label}</FieldLabel>
    <Input
      autoFocus
      type={kind === 'date' ? 'date' : 'text'}
      inputMode={
        kind === 'date' ? undefined : kind === 'money' ? 'decimal' : 'numeric'
      }
      value={value}
      onChange={(event) => onChange(event.target.value.replace(',', '.'))}
      onKeyDown={(event) => event.key === 'Enter' && onDone()}
    />
  </FieldRoot>
)

export function useFieldGroups(
  availableConfiguration: ShippingConfiguration
): FieldGroup[] {
  const { t } = useTranslation()
  const fieldLabel = useFieldLabel()
  const fields = availableConfiguration.functions
    .map((f) => f.function)
    .filter((fn) => fn !== ALWAYS)
  return FIELD_GROUPS.map((group) => ({
    label: t(`edit_shipping.function_names.${group}`),
    items: fields
      .filter((fn) => fieldSpec(fn).group === group)
      .map((fn) => ({
        value: fn,
        label: fieldLabel(fn),
        detail: t(`shipping_rules.field_descriptions.${fn}`, {
          defaultValue: ''
        })
      }))
  }))
}
